import { Box, Chip, Typography } from '@mui/material';
import { Clear } from '@mui/icons-material';
import FilterChip from './FilterChip';
import useActiveFilters from './useActiveFilters';

import type { ColumnFilterWithDisplayLabel, FilterConfig } from '../TableHeader';

interface ActiveFilterChipsProps {
    filters: FilterConfig[];
    columnFilters: ColumnFilterWithDisplayLabel[];
    onFiltersChange: (filters: ColumnFilterWithDisplayLabel[]) => void;
}

/**
 * ActiveFilterChips renders a list of active filters and a button to clear all removable filters.
 *
 * @param {ActiveFilterChipsProps} props
 * @returns {JSX.Element}
 */
const ActiveFilterChips = ({ filters, columnFilters, onFiltersChange }: ActiveFilterChipsProps) => {
    const { permanentFilters, removableFilters, updateFilter, clearAllFilters } = useActiveFilters(filters, columnFilters, onFiltersChange);

    const handleRemoveFilter = (filterId: string) => {
        updateFilter(filterId, '');
    };

    const hasAnyFilters = permanentFilters.length > 0 || removableFilters.length > 0;
    const removableFiltersCount = removableFilters.length;

    if (!hasAnyFilters) return null;

    return (
        <Box
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 1 }}
            role="group"
            aria-labelledby="active-filters"
        >
            {permanentFilters.length > 0 && (
                <>
                    <Typography variant="body2" color="text.secondary">
                        Displaying results for
                    </Typography>
                    {permanentFilters.map((filter) => (
                        <FilterChip key={filter.id} filter={filter} onRemove={handleRemoveFilter} />
                    ))}
                </>
            )}
            {removableFilters.length > 0 && (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: permanentFilters.length > 0 ? 1 : 0 }}>
                        Selected filters
                    </Typography>
                    {removableFilters.map((filter) => (
                        <FilterChip key={filter.id} filter={filter} onRemove={handleRemoveFilter} />
                    ))}
                    <Box sx={{ ml: 'auto' }}>
                        <Chip
                            variant="outlined"
                            color="primary"
                            size="small"
                            label={`Clear filters (${removableFiltersCount})`}
                            onClick={clearAllFilters}
                            onDelete={clearAllFilters}
                            deleteIcon={<Clear />}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default ActiveFilterChips;
